import { useCallback, useEffect, useMemo, useState } from "react";
import { IPlayer, IPendingGameParticipant } from "../../../types/types";
import {
  assignPendingParticipant,
  dismissPendingParticipant,
  fetchPendingParticipants,
} from "../services/updateServices";
import { useFetch } from "../../../hooks/useFetch";

export const formatPendingStat = (value?: number) =>
  value == null ? "—" : String(Math.round(value));

export const usePendingParticipants = () => {
  const { data: players, loading: playersLoading } = useFetch<IPlayer[]>("players");
  const [pending, setPending] = useState<IPendingGameParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlayerByPending, setSelectedPlayerByPending] = useState<Record<number, string>>(
    {},
  );
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchPendingParticipants();
      setPending(rows);
    } catch {
      setError("Odotusjonon lataus epäonnistui.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPending();
  }, [loadPending]);

  const playerOptions = useMemo(
    () =>
      (players ?? []).slice().sort((a, b) => {
        const team = a.teamId.localeCompare(b.teamId);
        return team !== 0 ? team : a.name.localeCompare(b.name);
      }),
    [players],
  );

  const selectPlayer = (pendingId: number, playerId: string) => {
    setSelectedPlayerByPending((prev) => ({
      ...prev,
      [pendingId]: playerId,
    }));
  };

  const handleAssign = async (pendingId: number) => {
    const playerId = selectedPlayerByPending[pendingId];
    if (!playerId) return;
    setBusyId(pendingId);
    try {
      await assignPendingParticipant(pendingId, playerId);
      await loadPending();
    } catch {
      setError("Pelaajan liittäminen epäonnistui.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (pendingId: number) => {
    setBusyId(pendingId);
    try {
      await dismissPendingParticipant(pendingId);
      await loadPending();
    } catch {
      setError("Hylkääminen epäonnistui.");
    } finally {
      setBusyId(null);
    }
  };

  return {
    pending,
    loading: loading || playersLoading,
    error,
    playerOptions,
    selectedPlayerByPending,
    selectPlayer,
    busyId,
    handleAssign,
    handleDismiss,
  };
};
